import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  ApiError,
  api,
  type AccountingEntity,
  type LoginInput,
  type SessionPayload,
  type SignupInput,
} from "../lib/api-client";
import { AuthContext, type AuthStatus } from "../lib/auth-context";

interface AuthProviderProps {
  children: ReactNode;
}

async function accountingEntitiesForSession(
  session: SessionPayload,
): Promise<AccountingEntity[]> {
  return session.active_organization_id ? api.listAccountingEntities() : [];
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [accountingEntities, setAccountingEntities] = useState<AccountingEntity[]>([]);
  const [initializationError, setInitializationError] = useState<string | null>(null);

  const refreshSession = useCallback(async () => {
    try {
      const nextSession = await api.getSession();
      const nextEntities = await accountingEntitiesForSession(nextSession);
      setSession(nextSession);
      setAccountingEntities(nextEntities);
      setStatus("authenticated");
      setInitializationError(null);
    } catch (error) {
      setSession(null);
      setAccountingEntities([]);
      if (error instanceof ApiError && error.status === 401) {
        setStatus("guest");
        setInitializationError(null);
        return;
      }
      setStatus("unavailable");
      setInitializationError(
        error instanceof Error ? error.message : "Tax2 could not check your session.",
      );
    }
  }, []);

  useEffect(() => {
    void Promise.resolve().then(refreshSession);
  }, [refreshSession]);

  const login = useCallback(async (input: LoginInput) => {
    const nextSession = await api.login(input);
    const nextEntities = await accountingEntitiesForSession(nextSession);
    setSession(nextSession);
    setAccountingEntities(nextEntities);
    setStatus("authenticated");
    setInitializationError(null);
  }, []);

  const signup = useCallback(async (input: SignupInput) => {
    const nextSession = await api.signup(input);
    const nextEntities = await accountingEntitiesForSession(nextSession);
    setSession(nextSession);
    setAccountingEntities(nextEntities);
    setStatus("authenticated");
    setInitializationError(null);
  }, []);

  const logout = useCallback(async () => {
    if (session) {
      await api.logout(session.csrf_token);
    }
    setSession(null);
    setAccountingEntities([]);
    setStatus("guest");
  }, [session]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      if (!session || organizationId === session.active_organization_id) {
        return;
      }
      const nextSession = await api.switchOrganization(
        organizationId,
        session.csrf_token,
      );
      const nextEntities = await api.listAccountingEntities();
      setSession(nextSession);
      setAccountingEntities(nextEntities);
    },
    [session],
  );

  const switchAccountingEntity = useCallback(
    async (accountingEntityId: string) => {
      if (!session || accountingEntityId === session.active_accounting_entity_id) {
        return;
      }
      const nextSession = await api.switchAccountingEntity(
        accountingEntityId,
        session.csrf_token,
      );
      setSession(nextSession);
    },
    [session],
  );

  const value = useMemo(() => {
    const activeOrganization =
      session?.organizations.find(
        (organization) => organization.id === session.active_organization_id,
      ) ?? null;
    const activeAccountingEntity = session
      ? (accountingEntities.find(
          (entity) => entity.id === session.active_accounting_entity_id,
        ) ?? null)
      : null;

    return {
      status,
      session,
      accountingEntities,
      activeOrganization,
      activeAccountingEntity,
      initializationError,
      login,
      signup,
      logout,
      refreshSession,
      switchOrganization,
      switchAccountingEntity,
    };
  }, [
    accountingEntities,
    initializationError,
    login,
    logout,
    refreshSession,
    session,
    signup,
    status,
    switchAccountingEntity,
    switchOrganization,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
